import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import type { OwnerMood } from "../../../common/types.ts";
import { bySport } from "../../../common/sportFunctions.ts";

/**
 * Update teamSeason.ownerMood based on performance this season, only for user's team.
 *
 * This is based on three factors: regular season performance, playoff performance, and finances. Designed to be called after the playoffs end.
 *
 * @memberOf core.season
 * @return {Promise.Object} Resolves to an object containing the changes in teamSeason.ownerMood this season.
 */
export type OwnerEvaluation = {
	madePlayoffs: boolean;
	champion: boolean;
	won: number;
	// 0-based reverse-standings rank among non-playoff teams (0 = best draft odds); -1 if the
	// team made the playoffs. oddsQuality is 1 for the best odds, decaying to 0.
	oddsRank: number;
	numLotteryTeams: number;
	oddsQuality: number;
};

const updateOwnerMood = async (): Promise<
	| {
			cappedDeltas: OwnerMood;
			deltas: OwnerMood;
			evaluation: OwnerEvaluation;
	  }
	| undefined
> => {
	// If auto play seasons or multi team mode, no messages - keep in sync with genMessage
	if (
		local.autoPlayUntil ||
		g.get("spectator") ||
		g.get("userTids").length > 1
	) {
		return;
	}

	const t = await idb.getCopy.teamsPlus(
		{
			seasonAttrs: ["won", "playoffRoundsWon", "profit"],
			season: g.get("season"),
			tid: g.get("userTid"),
		},
		"noCopyCache",
	);

	if (!t) {
		return;
	}

	const teamSeason = await idb.cache.teamSeasons.indexGet(
		"teamSeasonsByTidSeason",
		[g.get("userTid"), g.get("season")],
	);

	if (!teamSeason) {
		return;
	}

	const salaryCapFactor =
		g.get("salaryCap") /
		bySport({
			// defaultGameAttributes.salaryCap, but frozen in time because otherwise various coefficients below would need to be updated when it changes
			baseball: 175000,
			basketball: 90000,
			football: 200000,
			hockey: 80000,
		});

	const expectedProfit = 15 * salaryCapFactor;

	const numPlayoffRounds = g.get("numGamesPlayoffSeries", "current").length;

	// Some sports are more random than others, so like a 60% winning percentage is more impressive then. I think it only matters a lot for baseball, so I picked that coeffiient by determining what factor is needed to make 110/162 wins as valuable as 70/82 wins, then adding a little more so like 95 win seasons are still pretty good.
	const winsFactor = bySport({
		baseball: 2.2,
		basketball: 1,
		football: 1,
		hockey: 1,
	});

	// --- Draft-odds positioning (the tank payoff) ---
	// The owner grades on draft ODDS the same way our Report Cards do: a non-playoff team that
	// finishes low is running a purposeful rebuild, not failing. Odds come from reverse standings,
	// so rank the lottery (non-playoff) teams by wins ascending — rank 0 = worst record = best
	// odds. oddsQuality decays from 1 (best odds) to 0 by ~the 6th-best lottery slot; past that
	// you're just losing, which still hurts.
	const madePlayoffs = t.seasonAttrs.playoffRoundsWon >= 0;
	let oddsRank = -1;
	let numLotteryTeams = 0;
	if (!madePlayoffs) {
		const allTeamSeasons = await idb.cache.teamSeasons.indexGetAll(
			"teamSeasonsBySeasonTid",
			[[g.get("season")], [g.get("season"), "Z"]],
		);
		const lottery = allTeamSeasons
			.filter((ts) => (ts.playoffRoundsWon ?? -1) < 0)
			.sort((a, b) => a.won - b.won);
		numLotteryTeams = lottery.length;
		oddsRank = lottery.findIndex((ts) => ts.tid === g.get("userTid"));
	}
	const ODDS_DECAY = 6;
	const oddsQuality =
		madePlayoffs || oddsRank < 0 ? 0 : Math.max(0, 1 - oddsRank / ODDS_DECAY);
	const oddsCredit = 0.2 * oddsQuality;

	const deltas = {
		// On-court results, Report-Card style: winning pleases the owner, and for a non-playoff
		// team good draft odds (a purposeful low finish) offset the losing. The mediocre middle —
		// losing without securing odds — is left as punishing as before (oddsCredit ≈ 0 there).
		wins:
			(winsFactor * (0.25 * (t.seasonAttrs.won - g.get("numGames") / 2))) /
				(g.get("numGames") / 2) +
			oddsCredit,
		playoffs: 0,
		money: g.get("budget")
			? (t.seasonAttrs.profit - expectedProfit) / (100 * salaryCapFactor)
			: 0,
	};

	if (t.seasonAttrs.playoffRoundsWon < 0) {
		// Missing the playoffs stings — but a genuine rebuild (strong odds) waives the penalty in
		// proportion to how good those odds are, so tanking for the draft won't get you fired.
		deltas.playoffs = -0.2 * (1 - oddsQuality);
	} else if (t.seasonAttrs.playoffRoundsWon < numPlayoffRounds) {
		deltas.playoffs =
			(0.16 / numPlayoffRounds) * t.seasonAttrs.playoffRoundsWon;
	} else {
		deltas.playoffs = 0.2;
	}

	if (!teamSeason.ownerMood) {
		teamSeason.ownerMood = (g as any).ownerMood
			? (g as any).ownerMood
			: {
					money: 0,
					playoffs: 0,
					wins: 0,
				};
	}

	// This is just for TypeScript
	const ownerMood = teamSeason.ownerMood;
	if (!ownerMood) {
		throw new Error("Should never happen");
	}

	// Bound only the top - can't win the game by doing only one thing, but you can lose it by neglecting one thing
	const cappedDeltas = { ...deltas };

	if (ownerMood.money + cappedDeltas.money > 1) {
		cappedDeltas.money = 1 - ownerMood.money;
	}

	if (ownerMood.playoffs + cappedDeltas.playoffs > 1) {
		cappedDeltas.playoffs = 1 - ownerMood.playoffs;
	}

	if (ownerMood.wins + cappedDeltas.wins > 1) {
		cappedDeltas.wins = 1 - ownerMood.wins;
	}

	// Only update owner mood if grace period is over
	if (g.get("season") >= g.get("gracePeriodEnd") && !g.get("godMode")) {
		// Bound only the top - can't win the game by doing only one thing, but you can lose it by neglecting one thing
		ownerMood.money += cappedDeltas.money;
		ownerMood.playoffs += cappedDeltas.playoffs;
		ownerMood.wins += cappedDeltas.wins;
		await idb.cache.teamSeasons.put(teamSeason);
	}

	return {
		cappedDeltas,
		deltas,
		evaluation: {
			madePlayoffs,
			champion:
				numPlayoffRounds > 0 &&
				t.seasonAttrs.playoffRoundsWon >= numPlayoffRounds,
			won: t.seasonAttrs.won,
			oddsRank,
			numLotteryTeams,
			oddsQuality,
		},
	};
};

export default updateOwnerMood;
